import { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://upsc-backend-353e.onrender.com";

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [error, setError] = useState("");
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // 🔎 Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedAction, setSelectedAction] = useState("");

  const token = JSON.parse(localStorage.getItem("user"))?.token;

  useEffect(() => {
    if (!token) {
      setError("Token not found. Please log in again.");
      setLoading(false);
      return;
    }

    // ✅ Step 1: Check if user is superadmin
    axios
      .get(`${API_BASE}/api/admin/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.isSuperadmin) {
          setIsSuperadmin(true);

          // ✅ Step 2: Fetch logs if superadmin
          return axios.get(`${API_BASE}/api/admin/profile/logs`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } else {
          throw new Error("Access denied: Superadmin only.");
        }
      })
      .then((res) => {
        const logs = res.data || [];
        setLogs(logs);
        setFilteredLogs(logs);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.message || err.message || "Failed to load logs.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  // 🔎 Filter logic
  useEffect(() => {
    let filtered = logs;

    if (startDate) {
      const start = new Date(startDate);
      filtered = filtered.filter((log) => new Date(log.timestamp) >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      filtered = filtered.filter((log) => new Date(log.timestamp) <= end);
    }

    if (selectedAction) {
      filtered = filtered.filter(
        (log) => log.action.toLowerCase() === selectedAction.toLowerCase()
      );
    }

    setFilteredLogs(filtered);
  }, [startDate, endDate, selectedAction, logs]);

  if (loading) {
    return (
      <div className="bg-white p-4 rounded shadow mt-6 text-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!isSuperadmin) {
    return (
      <div className="bg-white p-4 rounded shadow mt-6 text-center">
        <h3 className="text-xl font-semibold mb-2 text-red-600">Access Denied</h3>
        <p className="text-gray-600">Only superadmins can view activity logs.</p>
      </div>
    );
  }

  // 🧠 Get unique actions for dropdown
  const uniqueActions = [...new Set(logs.map((log) => log.action))];

  return (
    <div className="bg-white p-4 rounded shadow mt-6">
      <h3 className="text-2xl font-semibold mb-4">📜 Activity Logs</h3>

      {/* 🔍 Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Start Date</label>
          <input
            type="date"
            className="border rounded px-2 py-1 w-full"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">End Date</label>
          <input
            type="date"
            className="border rounded px-2 py-1 w-full"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Action Type</label>
          <select
            className="border rounded px-2 py-1 w-full"
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
          >
            <option value="">All Actions</option>
            {uniqueActions.map((action, idx) => (
              <option key={idx} value={action}>
                {action}
              </option>
            ))}
          </select>
        </div>

        {(startDate || endDate || selectedAction) && (
          <button
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setSelectedAction("");
            }}
            className="mt-6 md:mt-0 bg-gray-200 hover:bg-gray-300 text-sm px-4 py-2 rounded"
          >
            Clear Filters
          </button>
        )}
      </div>

      {error && <p className="text-red-500">{error}</p>}

      {filteredLogs.length === 0 ? (
        <p className="text-gray-500 italic">No activity logs match your filters.</p>
      ) : (
        <ul className="space-y-2 text-sm max-h-[500px] overflow-y-auto border-t pt-2">
          {filteredLogs.map((log, index) => (
            <li key={log._id || index} className="border-b pb-2">
              <code className="text-gray-800 block">
                🕒 <strong>{new Date(log.timestamp).toLocaleString()}</strong>
              </code>
              <code className="text-gray-700 block">
                🔧 <strong>{log.action}</strong>
              </code>
              <code className="text-gray-600 block">
                👤 {log.performedBy?.email} ({log.performedBy?.role || "N/A"})
              </code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActivityLogs;
